import { Box, Button, Paper, Popper, Tooltip, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import { ProductSummary } from '../../interfaces/Product/ProductSummary';
import { settingsAtom } from '../../atoms/Settings';
import { sharedColors } from '../../utils/Style';
import { AddToBasketPopper } from '../../components/ProductCard/AddToBasketPopper';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type AtCheckoutDiscountProductCardProps = {
  product: ProductSummary;
};

const AtCheckoutDiscountProductCard = ({
  product,
}: AtCheckoutDiscountProductCardProps) => {
  const t = useLocalizedText();

  const { primary, secondary, ternary } = useThemeColor();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <Link to={`/urun/${product.slug}`} style={{ textDecoration: 'none' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          p: 1.5,
          boxSizing: 'border-box',
          m: 0.5,
          border: '1px solid',
          borderColor: sharedColors.gray4,
          borderRadius: '6px',
        }}
      >
        <Box
          component='img'
          src={product.imagePath}
          width='auto'
          height={100}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Tooltip title={product.name}>
            <Typography
              sx={{
                maxWidth: 200,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                fontSize: '14px',
                fontWeight: 600,
                color: sharedColors.gray5,
              }}
            >
              {product.name}
            </Typography>
          </Tooltip>

          <Typography sx={{ color: primary, fontSize: '12px' }}>
            {t('basket.at_checkout_discount.special_discount')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: sharedColors.gray4,
                textDecoration: 'line-through',
              }}
            >
              {product.priceFormatted}
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: primary,
              }}
            >
              {product.atCheckoutPriceFormatted}
            </Typography>
          </Box>

          <Button
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();

              setAnchorEl(ev.currentTarget);
            }}
            sx={{
              fontWeight: 600,
              fontSize: '13px',
              padding: '8px 15px',
              width: 'fit-content',
              color: secondary,
              backgroundColor: primary,
              transition: 'background-color 0.3s ease',
              '&:hover': {
                backgroundColor: ternary,
              },
              textTransform: 'none',
              mt: 0.5,
            }}
          >
            {t('product.add_to_cart')}
          </Button>

          <Popper open={!!anchorEl} anchorEl={anchorEl}>
            <AddToBasketPopper
              productSlug={product.slug}
              onClose={() => setAnchorEl(null)}
              atCheckout
            />
          </Popper>
        </Box>
      </Box>
    </Link>
  );
};

type AtCheckoutDiscountCardProps = {
  products: ProductSummary[];
};

export const AtCheckoutDiscountCard = ({
  products,
}: AtCheckoutDiscountCardProps) => {
  const { primary } = useThemeColor();

  const settings = useRecoilValue(settingsAtom);

  return (
    <Paper
      elevation={3}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 1.5,
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 0.5,
        }}
      >
        <OfflineBoltIcon sx={{ color: primary }} />

        <Typography sx={{ fontWeight: 600 }}>
          {settings.settingsV1.at_checkout_discount_text}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          overflowX: 'auto',
          gap: 1.5,
        }}
      >
        {products.map((product) => (
          <AtCheckoutDiscountProductCard key={product.id} product={product} />
        ))}
      </Box>
    </Paper>
  );
};
